import React, { type MouseEvent } from "react";
import { toast } from "@heroui/react";
import { FormattedMessage } from "react-intl";

/**
 * Writes text to the clipboard, catching failures (e.g. Chrome's
 * "Document is not focused" NotAllowedError, which fires when focus shifted
 * between the triggering click and the async write - Sentry BINALE-APP-427)
 * instead of letting them surface as an unhandled rejection. Callers decide
 * how to surface a failure in their own UI idiom (toast, tooltip, etc.).
 */
export const writeToClipboard = async (text: string): Promise<boolean> => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
};

export const copyToClipboard = async (target: HTMLElement): Promise<boolean> => {
    target.style.userSelect = "text";
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(target);
    selection.removeAllRanges();
    selection.addRange(range);
    return writeToClipboard(target.textContent);
};

export const handleCopyField = async (e: MouseEvent<HTMLElement>, messageText: string) => {
    const text = e.currentTarget.textContent;
    if (text) {
        const target = e.currentTarget;
        const oldUserSelect = target.style.userSelect;
        if (await copyToClipboard(target)) {
            toast(messageText);
        } else {
            toast.danger(<FormattedMessage id="app.notification.copyToClipboardFailed" />);
        }
        target.style.userSelect = oldUserSelect;
    }
};
